// src/features/HRModule/utils/dashboardAnalytics.js

/**
 * Generic helper to count occurrences by a field.
 */
import { ONBOARDING_STAGES,ASSET_CATEGORIES,ASSET_STATUSES, LEAVE_STATUSES, DEPARTMENTS, LIFECYCLE_STATUSES } from "../constants/constants";
export const groupByCount = (data = [], key) => {
  return data.reduce((acc, item) => {
    const value = item[key];

    if (!value) return acc;

    acc[value] = (acc[value] || 0) + 1;

    return acc;
  }, {});
};

/**
 * Convert grouped object to chart array.
 */
export const toChartData = (groupedData, labelKey = "label", valueKey = "value") => {
  return Object.entries(groupedData).map(([label, value]) => ({
    [labelKey]: label,
    [valueKey]: value,
  }));
};

/* -------------------------------------------------------------------------- */
/*                                EMPLOYEES                                   */
/* -------------------------------------------------------------------------- */

export const getEmployeeStatusData = (employees = []) => {
  const totalEmployees = employees.length;

  return LIFECYCLE_STATUSES.map((status) => {
    const count = employees.filter(
      (employee) => employee.status === status
    ).length;

    const percentage = totalEmployees
      ? Number(((count / totalEmployees) * 100).toFixed(1))
      : 0;

    return {
      label: status,
      count,
      percentage,
    };
  });
};

export const getDepartmentDistribution = (employees = []) => {

  return DEPARTMENTS.map((department)=>({

    label: department,

    count: employees.filter(
      employee => employee.department === department
    ).length

  }));

};

export const getEmploymentTypeData = (employees = []) => {
  return toChartData(
    groupByCount(employees, "employment_type"),
    "employmentType",
    "count"
  );
};

/* -------------------------------------------------------------------------- */
/*                                  LEAVES                                    */
/* -------------------------------------------------------------------------- */

export const getLeaveStatusData = (leaveRequests = []) => {
  const totalLeaves = leaveRequests.length;

  return LEAVE_STATUSES.map((status) => {
    const count = leaveRequests.filter(
      (leave) => leave.status === status
    ).length;

    const percentage = totalLeaves
      ? Number(((count / totalLeaves) * 100).toFixed(1))
      : 0;

    return {
      label: status,
      count,
      percentage,
    };
  });
};

/* -------------------------------------------------------------------------- */
/*                                  ASSETS                                    */
/* -------------------------------------------------------------------------- */

export const getAssetStatusData = (assets = []) => {
  const totalAssets = assets.length;

  return ASSET_STATUSES.map((status) => {
    const count = assets.filter(
      (asset) => asset.status === status
    ).length;

    const percentage = totalAssets
      ? Number(((count / totalAssets) * 100).toFixed(1))
      : 0;

    return {
      label: status,
      count,
      percentage,
    };
  });
};

export const getAssetCategoryData = (assets = []) => {
  const totalAssets = assets.length;

  return ASSET_CATEGORIES.map((category) => {
    const count = assets.filter(
      (asset) => asset.asset_type === category
    ).length;

    const percentage = totalAssets
      ? Number(((count / totalAssets) * 100).toFixed(1))
      : 0;

    return {
      label: category,
      count,
      percentage,
    };
  });
};
/* -------------------------------------------------------------------------- */
/*                               ONBOARDING                                   */
/* -------------------------------------------------------------------------- */

export const getOnboardingStatusData = (onboarding = []) => {

 const counts = groupByCount(
    onboarding,
    "stage"
 );


 return ONBOARDING_STAGES.map((stage)=>({

    label:stage,

    count:counts[stage] || 0

 }));

};

/* -------------------------------------------------------------------------- */
/*                             EMPLOYEE GROWTH                                */
/* -------------------------------------------------------------------------- */

export const getEmployeeGrowthData = (
  employees = [],
  range = "6_MONTHS"
) => {

  const RANGE_MAP = {
    LAST_MONTH: 1,
    "3_MONTHS": 3,
    "6_MONTHS": 6,
    LAST_YEAR: 12,
  };

  const months = RANGE_MAP[range] || 6;

  const today = new Date();

  const monthlyData = [];

  for (let i = months - 1; i >= 0; i--) {

    const date = new Date(
      today.getFullYear(),
      today.getMonth() - i,
      1
    );

    monthlyData.push({
      month: date.toLocaleString("default", {
        month:"short"
      }),
      year: date.getFullYear(),
      monthIndex: date.getMonth(),
    });
  }


  const points = monthlyData.map((item)=>{

    const monthEnd = new Date(
      item.year,
      item.monthIndex + 1,
      0
    );


    const headcount = employees.filter(
      (employee)=>
        new Date(employee.joining_date) <= monthEnd
    ).length;


    return {
      month:item.month,
      headcount
    };

  });


  const first = points[0]?.headcount || 0;
  const last = points[points.length-1]?.headcount || 0;


  const growthPct =
    first
      ? Number((((last-first)/first)*100).toFixed(1))
      : 0;


  return {
    points,
    growthPct,
    rangeLabel: range
  };

};
/* -------------------------------------------------------------------------- */
/*                                 SUMMARY                                    */
/* -------------------------------------------------------------------------- */

export const getDashboardSummary = ({
  employees = [],
  leaveRequests = [],
  assets = [],
  onboarding = [],
}) => ({
  totalEmployees: employees.length,

  totalDepartments: new Set(
    employees.map((e) => e.department)
  ).size,

  totalLeaveRequests: leaveRequests.length,

  totalAssets: assets.length,

  totalOnboarding: onboarding.length,
});
